/**
 * PATH: packages/ui/src/kanban/components/PeoplePicker.tsx
 * FILE_ID: UI-KANBAN-PEOPLEPICKER-001
 * PURPOSE: One reusable "pick people" primitive — search box + scrollable
 *          list with avatars, multi-select checkmarks, and a busy spinner —
 *          shared by every people-choosing surface so they look and behave
 *          identically and live in one place.
 * WHY:
 *   The Kanban drawer Members section, the right-click "Assign to…"
 *   submenu, and the schedule team rail each hand-rolled their own
 *   slightly-different member dropdown (one had search, one didn't; one
 *   floated selected to the top, one didn't). Operator ask 2026-06-10:
 *   *"members in SAM there is team member logic … that should universally
 *   cross over with right-clicks and so on"*. This is that one widget.
 *
 * SOURCE-AGNOSTIC:
 *   Consumers map their own directory shape (Kanban ``KanbanMember`` =
 *   org login-users; schedule ``Employee`` = HR records) into the small
 *   ``PersonOption`` shape via an adapter, so the SAME picker backs both
 *   identity systems without knowing about either.
 */
import { Check, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { KanbanMember } from '../api'

/** The minimal person shape the picker renders. */
export interface PersonOption {
  id: number
  name: string
  email?: string | null
  /** Muted secondary line (role / trade / email). */
  subtitle?: string | null
  /** Optional avatar image; falls back to coloured initials. */
  avatarUrl?: string | null
}

/** Adapter: org login-user (Kanban) → ``PersonOption``. */
export function kanbanMemberToPerson(m: KanbanMember): PersonOption {
  return {
    id: m.id,
    name: m.name || m.email || `User ${m.id}`,
    email: m.email ?? null,
    subtitle: m.role ?? m.email ?? null,
    avatarUrl: m.avatar_url ?? null,
  }
}

export interface PeoplePickerProps {
  people: PersonOption[]
  /** Currently-selected ids (array or Set — both accepted). */
  selectedIds: number[] | Set<number>
  /** Fired on a row click with the id and whether it WILL be selected. */
  onToggle: (id: number, willSelect: boolean) => void | Promise<void>
  searchPlaceholder?: string
  /** Helper line under the search box (e.g. "Stays open for multiple"). */
  hint?: string
  emptyLabel?: string
  /** Row to show a spinner on (the one currently saving). */
  busyId?: number | null
  autoFocusSearch?: boolean
  /** Cap rows rendered (perf guard for very large orgs). Default 50. */
  maxItems?: number
  /** Tailwind max-height for the scroll area. Default ``max-h-[40vh]``. */
  maxHeightClassName?: string
  /** Float selected people to the top of the list. Default true. */
  selectedFirst?: boolean
  className?: string
}

function initials(p: PersonOption): string {
  const base = (p.name || p.email || '?').trim()
  const parts = base.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

function PersonAvatar({ person }: { person: PersonOption }) {
  if (person.avatarUrl) {
    return (
      <img
        src={person.avatarUrl}
        alt=""
        className="h-5 w-5 shrink-0 rounded-full object-cover"
      />
    )
  }
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[9px] font-semibold text-neutral-600 dark:bg-zinc-700 dark:text-neutral-200">
      {initials(person)}
    </span>
  )
}

/**
 * Search + multi-select people list. Controlled selection: the parent
 * owns ``selectedIds`` and reacts to ``onToggle`` (so it can save
 * optimistically / revert on failure). The picker only owns its search
 * text — deliberately, so it can be dropped into a menu, a drawer
 * section, or a popover without the host wiring search state.
 */
export function PeoplePicker({
  people,
  selectedIds,
  onToggle,
  searchPlaceholder = 'Search people…',
  hint,
  emptyLabel = 'No matching people.',
  busyId = null,
  autoFocusSearch = true,
  maxItems = 50,
  maxHeightClassName = 'max-h-[40vh]',
  selectedFirst = true,
  className,
}: PeoplePickerProps) {
  const [filter, setFilter] = useState('')

  const selected = useMemo(
    () => (selectedIds instanceof Set ? selectedIds : new Set(selectedIds)),
    [selectedIds],
  )

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const matched = q
      ? people.filter(
          (p) =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.email || '').toLowerCase().includes(q) ||
            (p.subtitle || '').toLowerCase().includes(q),
        )
      : people
    const sorted = [...matched].sort((a, b) => {
      if (selectedFirst) {
        const aOn = selected.has(a.id) ? 0 : 1
        const bOn = selected.has(b.id) ? 0 : 1
        if (aOn !== bOn) return aOn - bOn
      }
      return (a.name || a.email || '').localeCompare(b.name || b.email || '')
    })
    return sorted
  }, [people, filter, selected, selectedFirst])

  return (
    <div className={className}>
      <div className="border-b border-neutral-100 px-2 py-1.5 dark:border-zinc-800">
        <input
          autoFocus={autoFocusSearch}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-700 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-800 dark:bg-zinc-900 dark:text-neutral-200"
        />
        {hint && <p className="mt-1 text-[10px] text-neutral-400">{hint}</p>}
      </div>
      <div className={['overflow-y-auto', maxHeightClassName].join(' ')}>
        {visible.length === 0 && (
          <div className="px-3 py-2 text-[11px] text-neutral-500">
            {emptyLabel}
          </div>
        )}
        {visible.slice(0, maxItems).map((p) => {
          const on = selected.has(p.id)
          const isBusy = busyId === p.id
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => void onToggle(p.id, !on)}
              disabled={isBusy}
              className={[
                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-neutral-900 md:py-1.5',
                on ? 'bg-blue-50/60 dark:bg-blue-950/20' : '',
              ].join(' ')}
              data-testid={`people-picker-row-${p.id}`}
              aria-pressed={on}
            >
              <PersonAvatar person={p} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {p.name || p.email || `User ${p.id}`}
                </span>
                {p.subtitle && (
                  <span className="block truncate text-[10px] text-neutral-500">
                    {p.subtitle}
                  </span>
                )}
              </span>
              {isBusy ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-400" />
              ) : on ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default PeoplePicker
